/**
 * Partner Repository
 * Extends BaseRepository for partner data access
 */

import { Partner, Shop } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity,
    PaginatedResult
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    startAfter,
    limit,
    getDoc,
    getDocs,
    doc as firestoreDoc,
    setDoc,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import { reviewRepository } from './reviewRepository';
import { QueryDocumentSnapshot, DocumentData, DocumentSnapshot } from 'firebase/firestore';

export interface PartnerEntity extends BaseEntity {
    firebaseUid: string;
    email: string;
    name: string;
    phone: string;
    dni: string;
    type: 'individual' | 'business';
    businessName?: string;
    membership: 'free' | 'basic' | 'pro' | 'premium';
    membershipStartedAt?: string;
    membershipExpiresAt?: string;
    autoRenew: boolean;
    balance: number;
    pendingBalance: number;
    totalSales: number;
    totalCommissionPaid: number;
    commissionRate: number;
    isVerified: boolean;
    status: 'active' | 'pending' | 'pending_documents' | 'suspended' | 'rejected';
    contactInfo: {
        phone: string;
        email: string;
    };
    notificationPreferences: {
        email: boolean;
        sms: boolean;
        push: boolean;
    };
    shopId?: string;
    createdAt: string;
    updatedAt: string;
}

class PartnerRepository extends BaseRepository<PartnerEntity> {
    protected collectionName = COLLECTIONS.PARTNERS;
    private shopsColl = COLLECTIONS.SHOPS;

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): PartnerEntity {
        const data = doc.data();
        return {
            id: doc.id,
            firebaseUid: (data.firebaseUid as string) || '',
            email: (data.email as string) || '',
            name: (data.name as string) || '',
            phone: (data.phone as string) || '',
            dni: (data.dni as string) || '',
            type: (data.type as PartnerEntity['type']) || 'individual',
            businessName: data.businessName as string | undefined,
            membership: (data.membership as PartnerEntity['membership']) || 'free',
            membershipStartedAt: data.membershipStartedAt as string | undefined,
            membershipExpiresAt: data.membershipExpiresAt as string | undefined,
            autoRenew: !!data.autoRenew,
            balance: (data.balance as number) || 0,
            pendingBalance: (data.pendingBalance as number) || 0,
            totalSales: (data.totalSales as number) || 0,
            totalCommissionPaid: (data.totalCommissionPaid as number) || 0,
            commissionRate: (data.commissionRate as number) || 20,
            isVerified: !!data.isVerified,
            status: (data.status as PartnerEntity['status']) || 'pending',
            contactInfo: data.contactInfo as PartnerEntity['contactInfo'] || { phone: '', email: '' },
            notificationPreferences: data.notificationPreferences as PartnerEntity['notificationPreferences'] || { email: true, sms: false, push: true },
            shopId: data.shopId as string | undefined,
            createdAt: (data.createdAt as string) || new Date().toISOString(),
            updatedAt: (data.updatedAt as string) || new Date().toISOString()
        };
    }

    // ==================== PARTNER-SPECIFIC METHODS ====================

    /**
     * Get partner by Firebase UID
     */
    async getPartnerByFirebaseUid(firebaseUid: string): Promise<PartnerEntity | undefined> {
        try {
            // Note: This is inefficient for large collections
            // Ideally, Firebase should have an index on firebaseUid
            const partners = await this.findAll(500);
            return partners.find(p => p.firebaseUid === firebaseUid);
        } catch (error) {
            logger.error(`[PartnerRepository] Error fetching partner by UID ${firebaseUid}`, error);
            return undefined;
        }
    }

    /**
     * Create partner with auto-generated ID
     */
    async createPartner(partner: Omit<PartnerEntity, 'id'>): Promise<string> {
        const id = await this.create(partner);
        CacheInvalidator.invalidatePartner(id);
        return id;
    }

    /**
     * Update partner
     */
    async updatePartner(partnerId: string, updates: Partial<PartnerEntity>): Promise<void> {
        await this.update(partnerId, updates);
        CacheInvalidator.invalidatePartner(partnerId);
    }

    /**
     * Get all partners with pagination
     */
    async getAllPartners(
        lastVisible: QueryDocumentSnapshot<DocumentData> | null = null,
        pageSize: number = 20
    ): Promise<{ partners: PartnerEntity[], lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
        try {
            if (lastVisible) {
                const q = query(
                    this.collection,
                    orderBy('createdAt', 'desc'),
                    startAfter(lastVisible),
                    firestoreLimit(pageSize)
                );
                const snapshot = await getDocs(q);
                const partners = snapshot.docs.map(doc => this.toEntity(doc));
                const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
                return { partners, lastDoc };
            } else {
                const q = query(
                    this.collection,
                    orderBy('createdAt', 'desc'),
                    firestoreLimit(pageSize)
                );
                const snapshot = await getDocs(q);
                const partners = snapshot.docs.map(doc => this.toEntity(doc));
                const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
                return { partners, lastDoc };
            }
        } catch (error) {
            logger.error('[PartnerRepository] Error fetching partners', error);
            return { partners: [], lastDoc: null };
        }
    }

    /**
     * Search partners by name, email, or DNI
     */
    async searchPartners(queryStr: string): Promise<PartnerEntity[]> {
        try {
            if (!queryStr || queryStr.length < 2) return [];

            const lowerQ = queryStr.toLowerCase();
            const partners = await this.findAll(100);

            return partners
                .filter(p =>
                    p.name.toLowerCase().includes(lowerQ) ||
                    p.email.toLowerCase().includes(lowerQ) ||
                    p.dni.includes(lowerQ)
                )
                .slice(0, 20);
        } catch (error) {
            logger.error('[PartnerRepository] Partner search failed', error);
            return [];
        }
    }

    /**
     * Get pending partners
     */
    async getPendingPartners(): Promise<PartnerEntity[]> {
        const partners = await this.findAll();
        return partners.filter(p => p.status === 'pending');
    }

    /**
     * Get partners by IDs
     */
    async getPartnersByIds(partnerIds: string[]): Promise<PartnerEntity[]> {
        const partners: PartnerEntity[] = [];

        for (const id of partnerIds) {
            const partner = await this.findById(id);
            if (partner) {
                partners.push(partner);
            }
        }

        return partners;
    }

    // ==================== SHOP-RELATED METHODS ====================
    // Note: These should ideally be moved to ShopRepository

    /**
     * Get shop by ID
     */
    async getShopById(id: string): Promise<Shop | undefined> {
        try {
            const docRef = firestoreDoc(db, this.shopsColl, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...(docSnap.data() as object) } as Shop;
            }
        } catch (error) {
            logger.error(`[PartnerRepository] Error fetching shop ${id}`, error);
        }
        return undefined;
    }

    /**
     * Get shop by slug
     */
    async getShopBySlug(slug: string): Promise<Shop | undefined> {
        try {
            const q = query(
                collection(db, this.shopsColl),
                where('slug', '==', slug),
                firestoreLimit(1)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                return { id: doc.id, ...(doc.data() as object) } as Shop;
            }
        } catch (error) {
            logger.error(`[PartnerRepository] Error fetching shop by slug ${slug}`, error);
        }
        return undefined;
    }

    /**
     * Get all shops with pagination
     */
  async getAllShops(
    pageSize?: number,
    filters?: { featured?: boolean; verified?: boolean },
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null
  ): Promise<{ shops: Shop[], lastDoc: QueryDocumentSnapshot<DocumentData> | null, hasMore: boolean }> {
    try {
      // Build constraints array - FILTERS must come BEFORE orderBy in Firestore
      const constraints: any[] = [];

      // Add filters first
      if (filters?.featured) {
        constraints.push(where('featured', '==', true));
      }

      if (filters?.verified) {
        constraints.push(where('isVerified', '==', true));
      }

      // Then orderBy and pagination
      constraints.push(orderBy('createdAt', 'desc'));

      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      constraints.push(firestoreLimit(pageSize || 20));

      const q = query(collection(db, this.shopsColl), ...constraints);
      const snapshot = await getDocs(q);
      const shops = snapshot.docs.map(d => ({ id: d.id, ...(d.data() as object) }) as Shop);

      return {
        shops,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData> | null || null,
        hasMore: shops.length === (pageSize || 20)
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('index') || (error as any).code === 'failed-precondition') {
          const indexFields = filters?.featured && filters?.verified
            ? '(featured ASC, isVerified ASC, createdAt DESC)'
            : filters?.featured
              ? '(featured ASC, createdAt DESC)'
              : filters?.verified
                ? '(isVerified ASC, createdAt DESC)'
                : '(createdAt DESC)';
          logger.error(`[PartnerRepository] Missing Firestore index. Create: ${indexFields}`, error);
        } else {
          logger.error('[PartnerRepository] Error fetching shops', error);
        }
      } else {
        logger.error('[PartnerRepository] Error fetching shops', new Error('Unknown error'));
      }
      return { shops: [], lastDoc: null, hasMore: false };
    }
  }

  /**
   * Get pending shops
   */
    async getPendingShops(): Promise<Shop[]> {
        try {
            const q = query(
                collection(db, this.shopsColl),
                where('status', '==', 'pending')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...(d.data() as object) }) as Shop);
        } catch (error) {
            logger.error('[PartnerRepository] Error fetching pending shops', error);
            return [];
        }
    }

    /**
     * Create shop
     */
    async createShop(shop: Shop): Promise<string> {
        const docRef = firestoreDoc(collection(db, this.shopsColl));
        await setDoc(docRef, shop);
        return docRef.id;
    }

    /**
     * Update shop
     */
    async updateShop(shopId: string, updates: Partial<Shop>): Promise<void> {
        const docRef = firestoreDoc(db, this.shopsColl, shopId);
        await updateDoc(docRef, { ...updates, updatedAt: new Date().toISOString() });
    }

    /**
     * Check if slug is available
     */
    async isSlugAvailable(slug: string): Promise<boolean> {
        const shop = await this.getShopBySlug(slug);
        return !shop;
    }

    // ==================== FOLLOW METHODS ====================
    // These should ideally be in followerRepository

    /**
     * Follow a shop
     */
    async followShop(shopId: string, userId: string): Promise<void> {
        const followerId = `${shopId}_${userId}`;
        const docRef = firestoreDoc(db, COLLECTIONS.SHOP_FOLLOWERS, followerId);
        await setDoc(docRef, {
            shopId,
            userId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
    }

    /**
     * Unfollow a shop
     */
    async unfollowShop(shopId: string, userId: string): Promise<void> {
        const followerId = `${shopId}_${userId}`;
        await deleteDoc(firestoreDoc(db, COLLECTIONS.SHOP_FOLLOWERS, followerId));
    }

    /**
     * Check if user follows shop
     */
    async isFollowingShop(shopId: string, userId: string): Promise<boolean> {
        const followerId = `${shopId}_${userId}`;
        const docRef = firestoreDoc(db, COLLECTIONS.SHOP_FOLLOWERS, followerId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists();
    }

    /**
     * Get shops followed by user
     */
    async getShopsFollowingByUser(userId: string): Promise<Shop[]> {
        try {
            const q = query(
                collection(db, COLLECTIONS.SHOP_FOLLOWERS),
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);

            const shopIds = snapshot.docs
                .map(d => d.data() as { userId: string; shopId: string })
                .filter(d => d.userId === userId)
                .map(d => d.shopId);

            if (shopIds.length === 0) return [];

            const shops: Shop[] = [];
            for (const shopId of shopIds) {
                const shop = await this.getShopById(shopId);
                if (shop && shop.status === 'active') {
                    shops.push(shop);
                }
            }

            return shops;
        } catch (error) {
            logger.error(`[PartnerRepository] Error getting shops followed by user ${userId}`, error);
            return [];
        }
    }

    /**
     * Get following shop IDs
     */
    async getFollowingShopIds(userId: string): Promise<string[]> {
        try {
            const q = query(
                collection(db, COLLECTIONS.SHOP_FOLLOWERS),
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);

            return snapshot.docs
                .map(d => d.data() as { userId: string; shopId: string })
                .filter(d => d.userId === userId)
                .map(d => d.shopId);
        } catch (error) {
            logger.error(`[PartnerRepository] Error getting following shop IDs for user ${userId}`, error);
            return [];
        }
    }

    // ==================== REVIEW METHODS ====================
    // These should ideally be in reviewRepository

    /**
     * Get product reviews by partner
     */
    async getProductReviewsByPartner(partnerId: string): Promise<unknown[]> {
        // Placeholder - would query productReviews collection
        return [];
    }

    /**
     * Get shop reviews by partner
     */
    async getShopReviewsByPartner(partnerId: string): Promise<unknown[]> {
        // Placeholder - would query shopReviews collection
        return [];
    }

    /**
     * Respond to product review
     */
    async respondToProductReview(reviewId: string, response: string): Promise<void> {
        // Placeholder - would update review in productReviews collection
    }

    /**
     * Respond to shop review
     */
    async respondToShopReview(reviewId: string, response: string): Promise<void> {
        // Placeholder - would update review in shopReviews collection
    }

  /**
   * Get pending product reviews
   */
  async getPendingProductReviews(): Promise<unknown[]> {
    return await reviewRepository.getPendingProductReviews();
  }

  /**
   * Get pending shop reviews
   */
  async getPendingShopReviews(): Promise<unknown[]> {
    return await reviewRepository.getPendingShopReviews();
  }

  /**
   * Get product reviews by status
   */
  async getProductReviewsByStatus(status: 'pending' | 'approved' | 'rejected'): Promise<unknown[]> {
    return await reviewRepository.getProductReviewsByStatus(status);
  }

  /**
   * Get shop reviews by status
   */
  async getShopReviewsByStatus(status: 'pending' | 'approved' | 'rejected'): Promise<unknown[]> {
    return await reviewRepository.getShopReviewsByStatus(status);
  }

  /**
   * Approve product review
   */
  async approveProductReview(reviewId: string): Promise<void> {
    await reviewRepository.approveProductReview(reviewId);
  }

  /**
   * Approve shop review
   */
  async approveShopReview(reviewId: string): Promise<void> {
    await reviewRepository.approveShopReview(reviewId);
  }

    /**
     * Reject product review
     */
    async rejectProductReview(reviewId: string, reason?: string): Promise<void> {
        // Placeholder - would update review status
    }

    /**
     * Reject shop review
     */
    async rejectShopReview(reviewId: string, reason?: string): Promise<void> {
        // Placeholder - would update review status
    }
}

export const partnerRepository = new PartnerRepository();
